import React, { useEffect, useState, useMemo } from "react";

// ===================================================================
//  CONFETTI PARTICLES — Pure CSS, no external libraries
//  Brief 2s burst of colored particles on arrival
// ===================================================================

const PARTICLE_COUNT = 40;
const DURATION_MS = 2000;

const COLORS = [
  "#10B981", // green
  "#34D399", // light green
  "#F59E0B", // amber
  "#3498DB", // blue
  "#8B5CF6", // purple
  "#EF4444", // red
  "#06B6D4", // cyan
  "#EC4899", // pink
];

interface Particle {
  id: number;
  color: string;
  x: number; // start x %
  y: number; // start y %
  dx: number; // horizontal drift px
  dy: number; // vertical distance px
  size: number;
  delay: number;
  rotation: number;
  shape: "circle" | "square" | "strip";
}

const generateParticles = (): Particle[] => {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    x: 40 + Math.random() * 20, // Center-ish
    y: 30 + Math.random() * 20,
    dx: (Math.random() - 0.5) * 300,
    dy: -(100 + Math.random() * 200),
    size: 4 + Math.random() * 8,
    delay: Math.random() * 400,
    rotation: Math.random() * 720 - 360,
    shape: (["circle", "square", "strip"] as const)[Math.floor(Math.random() * 3)],
  }));
};

interface ConfettiParticlesProps {
  active: boolean;
}

const ConfettiParticles: React.FC<ConfettiParticlesProps> = ({ active }) => {
  const [visible, setVisible] = useState(false);
  const particles = useMemo(() => generateParticles(), []);

  useEffect(() => {
    if (active) {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), DURATION_MS + 500);
      return () => clearTimeout(timer);
    }
    setVisible(false);
  }, [active]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 50,
        overflow: "hidden",
      }}
    >
      {particles.map((p) => {
        const borderRadius =
          p.shape === "circle"
            ? "50%"
            : p.shape === "strip"
            ? "2px"
            : "1px";

        const width = p.shape === "strip" ? p.size * 0.4 : p.size;
        const height = p.shape === "strip" ? p.size * 1.5 : p.size;

        return (
          <div
            key={p.id}
            style={{
              position: "absolute",
              left: `${p.x}%`,
              top: `${p.y}%`,
              width,
              height,
              backgroundColor: p.color,
              borderRadius,
              opacity: 0,
              animation: `confettiBurst ${DURATION_MS}ms ease-out ${p.delay}ms forwards`,
              // Custom properties for the animation
              ["--dx" as string]: `${p.dx}px`,
              ["--dy" as string]: `${p.dy}px`,
              ["--rot" as string]: `${p.rotation}deg`,
            }}
          />
        );
      })}
    </div>
  );
};

export default React.memo(ConfettiParticles);
